/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";

import breakpoints from "assets/theme/base/breakpoints";

// Data
import ShortListTutorsTableData from "Views/Activities/ShortListTutors/data/ShortListTableData";
import {
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import ArgonButton from "components/ArgonButton";
// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import React, { useState, useEffect, useRef } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import api from "utils/api";
import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useLoadScript,
  OverlayView,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import { CircularProgress, Alert, Snackbar } from "@mui/material";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import defaultAvatar from "assets/images/team-2.jpg";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

// Google Maps API Key - Replace with your own key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";
const GOOGLE_MAPS_LIBRARIES = ["places"];
const SHORTLIST_FILTER_STATE_KEY = "edushala.shortlistTutors.filters";

const loadShortlistFilterState = () => {
  try {
    const storedState = sessionStorage.getItem(SHORTLIST_FILTER_STATE_KEY);
    return storedState ? JSON.parse(storedState) : {};
  } catch (error) {
    console.warn("Unable to restore shortlist tutor filters:", error);
    return {};
  }
};

const normalizeIndiaCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return null;
  }

  const first = parseFloat(coordinates[0]);
  const second = parseFloat(coordinates[1]);

  if (Number.isNaN(first) || Number.isNaN(second)) {
    return null;
  }

  const isIndiaLat = (value) => value >= 6 && value <= 38;
  const isIndiaLng = (value) => value >= 68 && value <= 98;

  if (isIndiaLat(first) && isIndiaLng(second)) {
    return { lat: first, lng: second };
  }

  if (isIndiaLng(first) && isIndiaLat(second)) {
    return { lat: second, lng: first };
  }

  if (first >= -90 && first <= 90 && second >= -180 && second <= 180) {
    return { lat: first, lng: second };
  }

  if (second >= -90 && second <= 90 && first >= -180 && first <= 180) {
    return { lat: second, lng: first };
  }

  return null;
};

function Tables() {
  const restoredFilterState = useRef(loadShortlistFilterState());

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const { columns, rows } = ShortListTutorsTableData;

  const [searchMode, setSearchMode] = useState(
    restoredFilterState.current.searchMode || "PARENT"
  );
  const [Enquiry, setEnquiry] = useState(restoredFilterState.current.Enquiry || "");
  const [selectedStudents, setSelectedStudents] = useState(
    restoredFilterState.current.selectedStudents || []
  );
  const [Status, setStatus] = useState("");
  const [Academic, setAcademic] = useState(restoredFilterState.current.Academic || []);
  const [Gender, setGender] = useState(restoredFilterState.current.Gender || "");
  const [BadgeLevel, setBadgeLevel] = useState(restoredFilterState.current.BadgeLevel || []);
  const [fluency, setFluency] = useState(restoredFilterState.current.fluency || 0);
  const [radius, setRadius] = useState(restoredFilterState.current.radius || 0);
  const [parents, setParents] = useState([]);
  const [parentDetails, setParentDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [directSubjects, setDirectSubjects] = useState([]);
  const [directClasses, setDirectClasses] = useState([]);
  const [directClassId, setDirectClassId] = useState(
    restoredFilterState.current.directClassId || ""
  );
  const [directTuitionMode, setDirectTuitionMode] = useState(
    restoredFilterState.current.directTuitionMode || ""
  );
  const [filteredTutors, setFilteredTutors] = useState(
    restoredFilterState.current.filteredTutors || []
  );
  const [loading, setLoading] = useState(false);
  const [tutorSearch, setTutorSearch] = useState("");
  const searchQuery = tutorSearch.trim().toLowerCase();
  const phoneQuery = searchQuery.replace(/\D/g, "");
  const visibleTutors = filteredTutors.filter((tutor) =>
    !searchQuery ||
    String(tutor.fullName || "").toLowerCase().includes(searchQuery) ||
    (phoneQuery && /^[+\d\s().-]+$/.test(searchQuery) &&
      String(tutor.phone || "").replace(/\D/g, "").includes(phoneQuery))
  );
  const [selectedTutorIds, setSelectedTutorIds] = useState(
    restoredFilterState.current.selectedTutorIds || []
  );
  const [savingShortlist, setSavingShortlist] = useState(false);
  const [classPopupTutor, setClassPopupTutor] = useState(null);

  // Map states
  const [mapCenter, setMapCenter] = useState(
    restoredFilterState.current.mapCenter || { lat: 28.6139, lng: 77.209 }
  ); // Default to Delhi
  const [selectedLocation, setSelectedLocation] = useState(
    restoredFilterState.current.selectedLocation || null
  );
  const [directLocationLabel, setDirectLocationLabel] = useState(
    restoredFilterState.current.directLocationLabel || ""
  );
  const [mapZoom, setMapZoom] = useState(restoredFilterState.current.mapZoom || 13);
  const mapRef = useRef(null);
  const locationSearchBoxRef = useRef(null);
  const shouldPrefillStudentFiltersRef = useRef(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        SHORTLIST_FILTER_STATE_KEY,
        JSON.stringify({
          searchMode,
          Enquiry,
          selectedStudents,
          Academic,
          directClassId,
          directTuitionMode,
          Gender,
          BadgeLevel,
          fluency,
          radius,
          filteredTutors,
          selectedTutorIds,
          mapCenter,
          selectedLocation,
          directLocationLabel,
          mapZoom,
        })
      );
    } catch (error) {
      console.warn("Unable to save shortlist tutor filters:", error);
    }
  }, [
    Enquiry,
    searchMode,
    selectedStudents,
    Academic,
    directClassId,
    directTuitionMode,
    Gender,
    BadgeLevel,
    fluency,
    radius,
    filteredTutors,
    selectedTutorIds,
    mapCenter,
    selectedLocation,
    directLocationLabel,
    mapZoom,
  ]);

  useEffect(() => {
    const fetchDirectSearchSubjects = async () => {
      try {
        const response = await api.get("/tutor/search-subjects");
        if (response.data.success) {
          setDirectSubjects(
            (response.data.data || []).map((subject) => ({
              label: subject.name,
              value: subject._id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching subjects for direct tutor search:", error);
      }
    };

    fetchDirectSearchSubjects();
  }, []);

  useEffect(() => {
    const fetchDirectSearchClasses = async () => {
      try {
        const response = await api.get("/tutor/search-classes");
        if (response.data.success) {
          setDirectClasses(
            (response.data.data || []).map((classItem) => ({
              label: classItem.name,
              value: classItem._id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching classes for direct tutor search:", error);
      }
    };

    fetchDirectSearchClasses();
  }, []);

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const response = await api.get("/parent");
        if (response.data.success) {
          const parentOptions = response.data.data.map((parent) => ({
            label: parent.fullName,
            value: parent._id,
            location: parent.location,
          }));
          setParents([{ label: "Select Parent", value: "" }, ...parentOptions]);
        }
      } catch (error) {
        console.error("Error fetching parents:", error);
      }
    };
    fetchParents();
  }, []);

  // Fetch parent details and update map when parent is selected
  useEffect(() => {
    const fetchParentDetails = async () => {
      if (searchMode === "DIRECT") return;

      if (!Enquiry || Enquiry === "") {
        setParentDetails(null);
        setMapCenter({ lat: 28.6139, lng: 77.209 });
        setSelectedLocation(null);
        return;
      }

      try {
        const response = await api.get(`/parent?id=${Enquiry}`);

        if (response.data.success) {
          const parent = response.data.data;
          setParentDetails(parent);

          // Set map center and marker to parent's location
          if (
            parent &&
            parent.location &&
            parent.location.coordinates &&
            parent.location.coordinates.length === 2
          ) {
            const newCenter = normalizeIndiaCoordinates(parent.location.coordinates);
            if (newCenter) {
              setMapCenter(newCenter);
              setSelectedLocation(newCenter);
            } else {
              console.warn("Invalid coordinates for parent:", parent.location.coordinates);
            }
          } else {
            console.warn("Parent location not available or invalid:", parent?.location);
          }
        }
      } catch (error) {
        console.error("Error fetching parent details:", error);
      }
    };
    fetchParentDetails();
  }, [Enquiry, searchMode]);

  // Fetch students when parent is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!Enquiry) {
        setStudents([]);
        setSelectedStudents([]);
        setSubjects([]);
        return;
      }

      try {
        const response = await api.get(`/student?parentId=${Enquiry}`);
        if (response.data.success) {
          const studentOptions = response.data.data.map((student) => ({
            label: student.name,
            value: student._id,
            ...student,
          }));
          setStudents(studentOptions);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };
    fetchStudents();
  }, [Enquiry]);

  // Fetch subjects when students are selected
  useEffect(() => {
    const fetchSubjectsForStudents = async () => {
      if (!selectedStudents || selectedStudents.length === 0) {
        setSubjects([]);
        if (shouldPrefillStudentFiltersRef.current) {
          setAcademic([]);
          setGender("");
          shouldPrefillStudentFiltersRef.current = false;
        }
        return;
      }

      try {
        // Collect unique subjects from all selected students
        const allSubjects = new Map();
        const genderPreferences = [];

        for (const studentId of selectedStudents) {
          const response = await api.get(`/student?id=${studentId}`);
          if (response.data.success) {
            const studentData = response.data.data;
            const preferredGender = studentData.tutorGenderPreference || studentData.gender;
            if (preferredGender) {
              genderPreferences.push(preferredGender);
            }

            if (studentData.subjects && Array.isArray(studentData.subjects)) {
              studentData.subjects.forEach((subject) => {
                if (subject._id && subject.name) {
                  allSubjects.set(subject._id, {
                    label: subject.name,
                    value: subject._id,
                  });
                }
              });
            }
          }
        }

        const subjectOptions = Array.from(allSubjects.values());
        setSubjects(subjectOptions);

        if (shouldPrefillStudentFiltersRef.current) {
          setAcademic(subjectOptions.map((subject) => subject.value));

          const uniqueGenderPreferences = [...new Set(genderPreferences)];
          setGender(uniqueGenderPreferences.length === 1 ? uniqueGenderPreferences[0] : "");
          shouldPrefillStudentFiltersRef.current = false;
        }
      } catch (error) {
        console.error("Error fetching student details:", error);
        setSubjects([]);
        shouldPrefillStudentFiltersRef.current = false;
      }
    };
    fetchSubjectsForStudents();
  }, [selectedStudents]);

  // Apply filters and fetch tutors
  const handleApplyFilters = async () => {
    const isDirectSearch = searchMode === "DIRECT";

    if (!isDirectSearch && (!Enquiry || !selectedStudents || selectedStudents.length === 0)) {
      toast.error("Please select a parent and at least one student");
      return;
    }

    if (
      isDirectSearch &&
      (!Academic || Academic.length === 0) &&
      !directClassId
    ) {
      toast.error("Please select at least one subject or a class");
      return;
    }

    if (isDirectSearch && !selectedLocation) {
      toast.error("Please select a location on the map");
      return;
    }

    // Validate that parent has location if radius is being used
    if (radius > 0 && !selectedLocation) {
      toast.error("Parent location not available. Cannot apply radius filter.");
      return;
    }

    setLoading(true);

    try {
      // Parent mode searches once per student; direct mode searches once using
      // the manually selected subject and map location.
      const allTutorsMap = new Map();
      const searchTargets = isDirectSearch ? [null] : selectedStudents;

      for (const studentId of searchTargets) {
        const selectedStudent = students.find((student) => student?.value === studentId);
        const preferredDurationMinutes = Number(selectedStudent?.preferredTimeDuration || 0);
        const preferredClassesPerWeek = Number(selectedStudent?.noOfClassesPerWeek || 0);

        const params = { page: 1, limit: 100 };

        if (!isDirectSearch) {
          params.parentId = Enquiry;
          params.studentId = studentId;
        } else {
          params.includeUnverified = true;
          if (directClassId) params.classIds = directClassId;
          if (directTuitionMode) params.modeOfTuition = directTuitionMode;
        }

        if (preferredDurationMinutes > 0) {
          params.duration = preferredDurationMinutes;
        }

        if (preferredClassesPerWeek > 0) {
          params.teachingDaysPerWeek = preferredClassesPerWeek;
        }

        // Subject filter - make sure it's a valid string
        if (Academic && Array.isArray(Academic) && Academic.length > 0) {
          params.subjects = Academic.join(",");
        }

        // Gender filter - make sure it's a valid value
        if (Gender && Gender !== "" && Gender !== "Select Gender") {
          params.gender = Gender.trim();
        }

        // Badge/Level filter - handle multiple levels
        if (BadgeLevel && Array.isArray(BadgeLevel) && BadgeLevel.length > 0) {
          // Send as comma-separated string
          params.tutorLevel = BadgeLevel.join(",");
        }

        // English proficiency filter
        if (fluency && parseInt(fluency) > 0) {
          params.englishProficiency = parseInt(fluency);
        }

        // Use parent's location for distance calculation and radius filtering.
        if (selectedLocation) {
          params.lat = parseFloat(selectedLocation.lat);
          params.long = parseFloat(selectedLocation.lng);
          if (radius && parseFloat(radius) > 0) {
            params.radius = parseFloat(radius);
          }
        }

        console.log("🔍 Sending filter params for student:", studentId, params);

        const response = await api.get("/tutor/filter-for-parent", { params });

        console.log("📦 Response received:", response.data);

        if (response.data.success && response.data.data) {
          response.data.data.forEach((tutor) => {
            // Use tutorId if available, otherwise use _id
            const tutorKey = tutor.tutorId || tutor._id;
            if (!allTutorsMap.has(tutorKey)) {
              allTutorsMap.set(tutorKey, {
                ...tutor,
                _id: tutorKey, // Ensure _id is set for table display
              });
            }
          });
        } else {
          console.warn("⚠️ Invalid response format or no data:", response.data);
        }
      }

      const uniqueTutors = Array.from(allTutorsMap.values());

      // Sort by distance whenever the API returns it.
      if (uniqueTutors.some((tutor) => Number.isFinite(tutor.distance))) {
        uniqueTutors.sort((a, b) => {
          const distA = Number.isFinite(a.distance) ? a.distance : Infinity;
          const distB = Number.isFinite(b.distance) ? b.distance : Infinity;
          return distA - distB;
        });
      }

      setFilteredTutors(uniqueTutors);
      setSelectedTutorIds([]); // Clear selection when new filters are applied

      // Adjust map to show all markers
      if (uniqueTutors.length > 0 && selectedLocation) {
        // Collect all valid coordinates
        const allLocations = [selectedLocation];

        uniqueTutors.forEach((tutor) => {
          const tutorLocation = normalizeIndiaCoordinates(tutor.location?.coordinates);
          if (tutorLocation) {
            allLocations.push(tutorLocation);
          }
        });

        if (allLocations.length > 1) {
          // Calculate bounds
          const lats = allLocations.map((loc) => loc.lat);
          const lngs = allLocations.map((loc) => loc.lng);

          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

          setMapCenter({ lat: centerLat, lng: centerLng });

          // Calculate appropriate zoom level based on distance
          const latDiff = Math.max(...lats) - Math.min(...lats);
          const lngDiff = Math.max(...lngs) - Math.min(...lngs);
          const maxDiff = Math.max(latDiff, lngDiff);

          let zoom = 13;
          if (maxDiff > 0.5) zoom = 10;
          else if (maxDiff > 0.2) zoom = 11;
          else if (maxDiff > 0.1) zoom = 12;
          else if (maxDiff > 0.05) zoom = 13;
          else zoom = 14;

          setMapZoom(zoom);
        }
      }

      if (uniqueTutors.length === 0) {
        toast.info(
          "No tutors found matching your criteria. Try increasing the radius or adjusting filters."
        );
      }
    } catch (error) {
      console.error("❌ Error filtering tutors:", error);
      toast.error(error.response?.data?.message || "Error filtering tutors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox selection
  const handleTutorSelection = (tutorId) => {
    setSelectedTutorIds((prev) => {
      if (prev.includes(tutorId)) {
        return prev.filter((id) => id !== tutorId);
      } else {
        return [...prev, tutorId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = (e) => {
    const visibleTutorIds = visibleTutors.map((tutor) => tutor._id);
    if (e.target.checked) {
      setSelectedTutorIds((prev) => [...new Set([...prev, ...visibleTutorIds])]);
    } else {
      setSelectedTutorIds((prev) => prev.filter((id) => !visibleTutorIds.includes(id)));
    }
  };

  // Handle save shortlisted tutors
  const handleSaveShortlist = async () => {
    const isDirectSearch = searchMode === "DIRECT";

    if (!isDirectSearch && !Enquiry) {
      toast.error("Please select a parent first");
      return;
    }

    if (selectedTutorIds.length === 0) {
      toast.error("Please select at least one tutor to shortlist");
      return;
    }

    setSavingShortlist(true);

    try {
      const response = isDirectSearch
        ? await api.post("/shortlisted-tutors/save-search", {
            tutorIds: selectedTutorIds,
            subjectIds: Academic,
            location: selectedLocation,
            radius,
            filters: {
              gender: Gender,
              tutorLevels: BadgeLevel,
              englishProficiency: fluency,
              classIds: directClassId ? [directClassId] : [],
              modeOfTuition: directTuitionMode,
            },
          })
        : await api.post("/shortlisted-tutors/save", {
            parentId: Enquiry,
            tutorIds: selectedTutorIds,
            studentIds: selectedStudents,
          });

      if (response.data.success) {
        toast.success(
          `${response.data.message}${
            response.data.data?.duplicates
              ? ` (${response.data.data.duplicates.length} already shortlisted)`
              : ""
          }`
        );
        setSelectedTutorIds([]); // Clear selection after successful save
      }
    } catch (error) {
      console.error("Error saving shortlisted tutors:", error);
      toast.error(error.response?.data?.message || "Error saving shortlisted tutors");
    } finally {
      setSavingShortlist(false);
    }
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setTutorSearch("");
    sessionStorage.removeItem(SHORTLIST_FILTER_STATE_KEY);
    setEnquiry("");
    setSelectedStudents([]);
    setStatus("");
    setAcademic([]);
    setDirectClassId("");
    setDirectTuitionMode("");
    setGender("");
    setBadgeLevel("");
    setFluency(0);
    setRadius(0);
    setFilteredTutors([]);
    setSelectedTutorIds([]);
    setParentDetails(null);
    setStudents([]);
    setSubjects([]);
    setMapCenter({ lat: 28.6139, lng: 77.209 });
    setSelectedLocation(null);
    setDirectLocationLabel("");
    setMapZoom(13);
    toast.info("Filters reset successfully");
  };

  const handleLocationPlaceChanged = () => {
    const places = locationSearchBoxRef.current?.getPlaces?.() || [];
    const place = places[0];
    const placeLocation = place?.geometry?.location;

    if (!placeLocation) {
      toast.error("Please select a location from the suggestions");
      return;
    }

    const location = {
      lat: placeLocation.lat(),
      lng: placeLocation.lng(),
    };

    setSelectedLocation(location);
    setMapCenter(location);
    setMapZoom(15);
    setDirectLocationLabel(place.formatted_address || place.name || "Selected location");

    if (place.geometry.viewport && mapRef.current) {
      mapRef.current.fitBounds(place.geometry.viewport);
    }
  };

  // Format tutors data for table display
  const formatTutorsForTable = () => {
    return visibleTutors.map((tutor, index) => {
      // Calculate age from DOB if available
      let age = "N/A";
      if (tutor.dob) {
        const birthDate = new Date(tutor.dob);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
      }

      // Badge color mapping
      const badgeColors = {
        "Level 1": "error",
        "Level 2": "warning",
        "Level 3": "success",
        "Level 4": "info",
      };

      // Badge emoji mapping
      const badgeEmojis = {
        "Level 1": "🥉",
        "Level 2": "🥈",
        "Level 3": "🥇",
        "Level 4": "💎",
      };

      const tutorClasses =
        tutor.classes && tutor.classes !== "N/A"
          ? tutor.classes
              .split(",")
              .map((className) => className.trim())
              .filter(Boolean)
          : [];

      return {
        rowSx: tutor.isVerified === false || tutor.unverifyed
          ? {
              backgroundColor: "rgba(255, 235, 59, 0.18)",
              "&:hover": { backgroundColor: "rgba(255, 235, 59, 0.28)" },
            }
          : tutor.classBasedPricingMissing
          ? {
              backgroundColor: "rgba(255, 152, 0, 0.12)",
              "&:hover": { backgroundColor: "rgba(255, 152, 0, 0.18)" },
              "& td:first-of-type": {
                borderLeft: "4px solid #fb8c00",
              },
            }
          : tutor.isShortlisted
          ? {
              backgroundColor: "rgba(20, 152, 252, 0.08)",
              "&:hover": { backgroundColor: "rgba(20, 152, 252, 0.13)" },
              "& td:first-of-type": {
                borderLeft: "4px solid #1498fc",
              },
            }
          : {},
        "S.No.": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {index + 1}
          </ArgonTypography>
        ),
        Tutor: (
          <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
            <ArgonBox mr={2}>
              <ArgonAvatar src={tutor.profileImage} alt={tutor.fullName} size="sm" variant="full" />
            </ArgonBox>
            <ArgonBox display="flex" flexDirection="column">
              <ArgonBox display="flex" alignItems="center" gap={0.75}>
                <ArgonTypography variant="button" fontWeight="medium">
                  <Link
                    to={`/tutors/profile/${tutor._id}`}
                    style={{ textDecoration: "underline", color: "inherit" }}
                  >
                    {tutor.fullName}
                  </Link>
                </ArgonTypography>
                <Tooltip title="View classes">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setClassPopupTutor({
                        name: tutor.fullName || "Tutor",
                        classes: tutorClasses,
                      })
                    }
                    sx={{
                      width: 22,
                      height: 22,
                      color: "#1498fc",
                      border: "1px solid rgba(20, 152, 252, 0.35)",
                      backgroundColor: "rgba(20, 152, 252, 0.08)",
                      "&:hover": { backgroundColor: "rgba(20, 152, 252, 0.16)" },
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </ArgonBox>
              <ArgonTypography variant="caption" color="secondary">
                {tutor.email || "No email"}
              </ArgonTypography>
              {(tutor.isShortlisted ||
                tutor.classBasedPricing ||
                tutor.isVerified === false ||
                tutor.unverifyed) && (
                <ArgonBox display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {(tutor.isVerified === false || tutor.unverifyed) && (
                    <ArgonBadge
                      variant="gradient"
                      color="warning"
                      badgeContent="Unverified"
                      size="xs"
                      container
                    />
                  )}
                  {tutor.isShortlisted && (
                    <ArgonBadge
                      variant="gradient"
                      color="info"
                      badgeContent="Shortlisted"
                      size="xs"
                      container
                    />
                  )}
                  {tutor.classBasedPricing && (
                    <ArgonBadge
                      variant="gradient"
                      color={tutor.classBasedPricingMissing ? "error" : "warning"}
                      badgeContent={tutor.classBasedPricingLabel || "Class based pricing"}
                      size="xs"
                      container
                    />
                  )}
                </ArgonBox>
              )}
            </ArgonBox>
          </ArgonBox>
        ),
        "Mobile No.": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {tutor.phone}
          </ArgonTypography>
        ),
        Age: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {age}
          </ArgonTypography>
        ),
        Distance: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {Number.isFinite(Number(tutor.distance))
              ? `${Number(tutor.distance).toFixed(1)} km`
              : "N/A"}
          </ArgonTypography>
        ),
        Badge: tutor.tutorLevel ? (
          <ArgonBadge
            variant="gradient"
            badgeContent={`${badgeEmojis[tutor.tutorLevel]} ${tutor.tutorLevel}`}
            color={badgeColors[tutor.tutorLevel]}
            size="xs"
            container
          />
        ) : (
          <ArgonTypography variant="caption" color="text">
            No Level
          </ArgonTypography>
        ),
        "Fee Per Session": (
          <ArgonBox display="flex" flexDirection="column" alignItems="center">
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              ₹{tutor.perSessionParentFee || tutor.hourlyRate || 0}
            </ArgonTypography>
            {tutor.classBasedPricingMissing && (
              <ArgonTypography variant="caption" color="error" fontWeight="bold">
                Add pricing
              </ArgonTypography>
            )}
            <ArgonTypography variant="caption" color="secondary">
              {tutor.requestedDurationMinutes
                ? `${tutor.requestedDurationMinutes} mins`
                : tutor.requestedDuration || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        "Tutor Salary": (
          <ArgonBox display="flex" flexDirection="column" alignItems="center">
            <Tooltip title="Tutor salary per session, excluding Edushala margin">
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {tutor.perSessionTutorSalary != null || tutor.tutorSalaryPerSession != null
                  ? `₹${tutor.perSessionTutorSalary ?? tutor.tutorSalaryPerSession}`
                  : "N/A"}
              </ArgonTypography>
            </Tooltip>
            {tutor.classBasedPricingMissing && (
              <ArgonTypography variant="caption" color="error" fontWeight="bold">
                Add pricing
              </ArgonTypography>
            )}
            <ArgonTypography variant="caption" color="secondary">
              {tutor.requestedDurationMinutes
                ? `${tutor.requestedDurationMinutes} mins`
                : tutor.requestedDuration || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        Select: (
          <Checkbox
            checked={selectedTutorIds.includes(tutor._id)}
            onChange={() => handleTutorSelection(tutor._id)}
            color="primary"
          />
        ),
      };
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            {/* Header */}
            <CardHeader
              title={
                <ArgonTypography variant="body2" fontWeight="medium">
                  Shortlist Tutors
                </ArgonTypography>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />

            <CardContent>
              <Grid container spacing={3}>
                {/* Left Side - Form */}
                <Grid item xs={12} md={6}>
                  <form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Search Mode
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={searchMode}
                          onChange={(e) => {
                            const nextMode = e.target.value;
                            setSearchMode(nextMode);
                            setAcademic([]);
                            setDirectClassId("");
                            setDirectTuitionMode("");
                            setFilteredTutors([]);
                            setSelectedTutorIds([]);

                            if (nextMode === "DIRECT") {
                              setEnquiry("");
                              setSelectedStudents([]);
                              setParentDetails(null);
                              setMapCenter({ lat: 23.2599, lng: 77.4126 });
                              setSelectedLocation(null);
                              setDirectLocationLabel("");
                              setMapZoom(12);
                            }
                          }}
                          options={[
                            { label: "Existing Parent", value: "PARENT" },
                            { label: "Direct Search (No Parent)", value: "DIRECT" },
                          ]}
                          width={260}
                        />
                      </Grid>

                      {/* Enquiry */}
                      {searchMode === "PARENT" && <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Parent *
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Enquiry}
                          onChange={(e) => {
                            shouldPrefillStudentFiltersRef.current = true;
                            setAcademic([]);
                            setGender("");
                            setEnquiry(e.target.value);
                          }}
                          options={parents}
                          width={260}
                        />
                      </Grid>}

                      {/* Student */}
                      {searchMode === "PARENT" && <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text" mb={1}>
                          Students *
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={students}
                          getOptionLabel={(option) => option.label || option.name || ""}
                          value={students.filter((student) =>
                            selectedStudents.includes(student.value)
                          )}
                          onChange={(event, newValue) => {
                            const selectedIds = newValue.map((student) => student.value);
                            shouldPrefillStudentFiltersRef.current = true;
                            setSelectedStudents(selectedIds);
                          }}
                          disabled={!Enquiry || students.length === 0}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label || option.name}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                students.length === 0 ? "No students available" : "Select students"
                              }
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>}

                      {/* Status */}
                      {/* <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Status
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Status}
                          onChange={(e) => setStatus(e.target.value)}
                          options={["Status 1", "Status 2", "Status 3"]}
                          width={260}
                        />
                      </Grid> */}

                      {/* Subject */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Subject (Optional)
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={searchMode === "DIRECT" ? directSubjects : subjects}
                          getOptionLabel={(option) => option.label || ""}
                          value={(searchMode === "DIRECT" ? directSubjects : subjects).filter(
                            (subject) => Academic.includes(subject.value)
                          )}
                          onChange={(event, newValue) => {
                            const selectedSubjectIds = newValue.map((subject) => subject.value);
                            setAcademic(selectedSubjectIds);
                          }}
                          disabled={
                            searchMode === "PARENT" &&
                            (!selectedStudents || selectedStudents.length === 0)
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                (searchMode === "DIRECT" ? directSubjects : subjects).length === 0
                                  ? "No subjects"
                                  : "Select subjects"
                              }
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {searchMode === "DIRECT" && (
                        <Grid item xs={12} sm={6}>
                          <ArgonTypography variant="button" fontWeight="regular" color="text">
                            Class
                          </ArgonTypography>
                          <Autocomplete
                            options={directClasses}
                            getOptionLabel={(option) => option.label || ""}
                            value={
                              directClasses.find((classItem) => classItem.value === directClassId) ||
                              null
                            }
                            onChange={(event, newValue) =>
                              setDirectClassId(newValue?.value || "")
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select class"
                                size="small"
                              />
                            )}
                            sx={{
                              width: 260,
                              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                            }}
                          />
                        </Grid>
                      )}

                      {searchMode === "DIRECT" && (
                        <Grid item xs={12} sm={6}>
                          <ArgonTypography variant="button" fontWeight="regular" color="text">
                            Teaching Mode
                          </ArgonTypography>
                          <Dropdown
                            label=""
                            value={directTuitionMode}
                            onChange={(event) => setDirectTuitionMode(event.target.value)}
                            options={[
                              { label: "Select Mode", value: "" },
                              { label: "Offline", value: "OFFLINE" },
                              { label: "Online", value: "ONLINE" },
                              { label: "Both", value: "BOTH" },
                            ]}
                            width={260}
                          />
                        </Grid>
                      )}

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Gender
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Gender}
                          onChange={(e) => setGender(e.target.value)}
                          options={[
                            { label: "Select Gender", value: "" },
                            { label: "Male", value: "MALE" },
                            { label: "Female", value: "FEMALE" },
                            { label: "Both", value: "BOTH" },
                          ]}
                          width={260}
                        />
                      </Grid>

                      {/* Badge/Level */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text" mb={1}>
                          Level/Badge (Multi-select)
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={[
                            { label: "🥉 Level 1", value: "Level 1" },
                            { label: "🥈 Level 2", value: "Level 2" },
                            { label: "🥇 Level 3", value: "Level 3" },
                            { label: "💎 Level 4", value: "Level 4" },
                          ]}
                          getOptionLabel={(option) => option.label}
                          value={[
                            { label: "🥉 Level 1", value: "Level 1" },
                            { label: "🥈 Level 2", value: "Level 2" },
                            { label: "🥇 Level 3", value: "Level 3" },
                            { label: "💎 Level 4", value: "Level 4" },
                          ].filter((option) => BadgeLevel.includes(option.value))}
                          onChange={(event, newValue) => {
                            const selectedLevels = newValue.map((level) => level.value);
                            setBadgeLevel(selectedLevels);
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select levels"
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {/* English Fluency */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          English Fluency ({fluency})
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={10}
                          value={fluency}
                          onChange={(e, newValue) => setFluency(newValue)}
                        />
                      </Grid>

                      {/* Radius */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Radius ({radius} Km)
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={25}
                          value={radius}
                          onChange={(e, newValue) => setRadius(newValue)}
                        />
                      </Grid>

                      {/* Submit */}
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        <ArgonButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={handleApplyFilters}
                          disabled={
                            loading ||
                            (searchMode === "PARENT"
                              ? !Enquiry || !selectedStudents || selectedStudents.length === 0
                              : !selectedLocation ||
                                (Academic.length === 0 && !directClassId))
                          }
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Apply Filters"
                          )}
                        </ArgonButton>
                        <ArgonButton
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleResetFilters}
                          disabled={loading}
                        >
                          Reset Filters
                        </ArgonButton>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>

                {/* Right Side - Map */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="h6" mb={2}>
                    {searchMode === "DIRECT" ? "Search Location" : "Parent Location"}{" "}
                    {searchMode === "PARENT" && parentDetails?.fullName && (
                      <span>
                        (
                        <Link
                          to={`/parents/profile/${Enquiry}`}
                          style={{ textDecoration: "underline", color: "inherit" }}
                        >
                          {parentDetails.fullName}
                        </Link>
                        )
                      </span>
                    )}
                  </ArgonTypography>
                  {searchMode === "DIRECT" && isLoaded && !loadError && (
                    <ArgonBox mb={1.5}>
                      <StandaloneSearchBox
                        onLoad={(searchBox) => {
                          locationSearchBoxRef.current = searchBox;
                        }}
                        onPlacesChanged={handleLocationPlaceChanged}
                      >
                        <input
                          type="text"
                          value={directLocationLabel}
                          onChange={(event) => setDirectLocationLabel(event.target.value)}
                          placeholder="Search area, landmark or full address"
                          aria-label="Search tutor location"
                          style={{
                            boxSizing: "border-box",
                            width: "100%",
                            height: "42px",
                            padding: "0 14px",
                            border: "1px solid #d2d6da",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "white",
                          }}
                        />
                      </StandaloneSearchBox>
                      <ArgonTypography variant="caption" color="text">
                      Select a suggestion by searching, or click directly on the map.
                      </ArgonTypography>
                    </ArgonBox>
                  )}
                  {searchMode === "PARENT" && (!Enquiry || Enquiry === "") ? (
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="400px"
                      bgcolor="#f5f5f5"
                      borderRadius="12px"
                    >
                      <ArgonTypography variant="body2" color="text" textAlign="center">
                        Please select a parent to view location
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <div style={{ height: "400px", borderRadius: "12px", overflow: "hidden" }}>
                      {loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                          p={3}
                        >
                          <ArgonTypography variant="body2" color="error" textAlign="center">
                            Map cannot be loaded. Please check your internet connection.
                          </ArgonTypography>
                        </ArgonBox>
                      )}
                      {!isLoaded && !loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                        >
                          <CircularProgress size={40} />
                        </ArgonBox>
                      )}
                      {isLoaded && !loadError && (
                        <GoogleMap
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          center={mapCenter}
                          zoom={mapZoom}
                          onClick={(event) => {
                            if (searchMode !== "DIRECT") return;
                            const location = {
                              lat: event.latLng.lat(),
                              lng: event.latLng.lng(),
                            };
                            setSelectedLocation(location);
                            setMapCenter(location);
                            setMapZoom(15);
                            setDirectLocationLabel(
                              `Pinned location (${location.lat.toFixed(5)}, ${location.lng.toFixed(
                                5
                              )})`
                            );
                          }}
                          onLoad={(map) => {
                            mapRef.current = map;
                          }}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            draggable: true,
                            zoomControl: true,
                            scrollwheel: true,
                          }}
                        >
                          {/* Parent Location Marker */}
                          {selectedLocation && (
                            <>
                              <OverlayView
                                position={selectedLocation}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    transform: "translate(-50%, -100%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={parentDetails?.profileImage || defaultAvatar}
                                    alt={parentDetails?.fullName}
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      borderRadius: "50%",
                                      border: "3px solid #FF0000",
                                      objectFit: "cover",
                                      backgroundColor: "white",
                                      cursor: "default",
                                    }}
                                  />
                                  <div
                                    style={{
                                      marginTop: "5px",
                                      backgroundColor: "white",
                                      padding: "8px 12px",
                                      borderRadius: "10px",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      minWidth: "120px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: "12px",
                                        color: "#FF0000",
                                      }}
                                    >
                                      {searchMode === "DIRECT"
                                        ? "Search location"
                                        : parentDetails?.fullName || "Parent"}
                                    </div>

                                    {/* Show parent area (use explicit field or extract from address) */}
                                    {(() => {
                                      const areaField = parentDetails?.area;
                                      const address = parentDetails?.address || "";
                                      const areaMatch = address.match(/Area:\s*([^,]+)/);
                                      const area =
                                        areaField || (areaMatch ? areaMatch[1].trim() : null);
                                      return area ? (
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#666",
                                            marginTop: "4px",
                                          }}
                                        >
                                          {area}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                              </OverlayView>
                            </>
                          )}

                          {/* Tutor Location Markers */}
                          {filteredTutors.map((tutor) => {
                            // Check if tutor has location data
                            if (!tutor.location || !tutor.location.coordinates) {
                              return null;
                            }

                            const normalizedLocation = normalizeIndiaCoordinates(
                              tutor.location.coordinates
                            );
                            if (!normalizedLocation) {
                              return null;
                            }

                            return (
                              <React.Fragment key={tutor._id}>
                                <OverlayView
                                  position={normalizedLocation}
                                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      transform: "translate(-50%, -100%)",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={tutor.profileImage || defaultAvatar}
                                      alt={tutor.fullName}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        borderRadius: "50%",
                                        border: "3px solid #1498FC",
                                        objectFit: "cover",
                                        backgroundColor: "white",
                                        cursor: "default",
                                      }}
                                    />
                                    <div
                                      style={{
                                        marginTop: "5px",
                                        backgroundColor: "white",
                                        padding: "8px 12px",
                                        borderRadius: "10px",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        minWidth: "120px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                          color: "#1498FC",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        {tutor.fullName || "Tutor"}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#7b809a",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {tutor.distance ? `${tutor.distance.toFixed(1)} km` : "N/A"}
                                      </div>
                                      {tutor.address &&
                                        (() => {
                                          const areaMatch = tutor.address.match(/Area:\s*([^,]+)/);
                                          const area = areaMatch ? areaMatch[1].trim() : null;
                                          return area ? (
                                            <div
                                              style={{
                                                fontSize: "10px",
                                                color: "#666",
                                              }}
                                            >
                                              {area}
                                            </div>
                                          ) : null;
                                        })()}
                                    </div>
                                  </div>
                                </OverlayView>
                              </React.Fragment>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  )}
                  {selectedLocation && parentDetails && (
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="body2" color="text">
                        {parentDetails.area && `${parentDetails.area}, `}
                        {parentDetails.city}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="secondary">
                        Coordinates: {selectedLocation.lat.toFixed(6)},{" "}
                        {selectedLocation.lng.toFixed(6)}
                      </ArgonTypography>
                    </ArgonBox>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </ArgonBox>

        {/* Table Section */}
        <ArgonBox mt={3} p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            <CardHeader
              title={
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Filtered Tutors ({visibleTutors.length})
                  </ArgonTypography>
                  <ArgonBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <TextField
                      size="small"
                      type="search"
                      placeholder="Search tutor name or phone"
                      value={tutorSearch}
                      onChange={(event) => setTutorSearch(event.target.value)}
                      inputProps={{ "aria-label": "Search tutor by name or phone number" }}
                      sx={{ width: { xs: "100%", sm: 200 } }}
                    />
                  {filteredTutors.length > 0 && (
                    <ArgonButton
                      variant="gradient"
                      color="success"
                      size="small"
                      onClick={handleSaveShortlist}
                      disabled={savingShortlist || selectedTutorIds.length === 0}
                    >
                      {savingShortlist ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        `Shortlist ${
                          selectedTutorIds.length > 0 ? `(${selectedTutorIds.length})` : ""
                        } Tutors`
                      )}
                    </ArgonButton>
                  )}
                  </ArgonBox>
                </ArgonBox>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />
            <CardContent>
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : visibleTutors.length > 0 ? (
                <ArgonBox>
                  {/* Select All Checkbox */}
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    p={2}
                    borderBottom="1px solid #eee"
                  >
                    <ArgonTypography variant="caption" color="text" fontWeight="medium" mr={1}>
                      Select All
                    </ArgonTypography>
                    <Checkbox
                      checked={
                        visibleTutors.every((tutor) => selectedTutorIds.includes(tutor._id))
                      }
                      indeterminate={
                        visibleTutors.some((tutor) => selectedTutorIds.includes(tutor._id)) &&
                        !visibleTutors.every((tutor) => selectedTutorIds.includes(tutor._id))
                      }
                      onChange={handleSelectAll}
                      color="primary"
                    />
                  </ArgonBox>
                  <ArgonBox
                    sx={{
                      "& .MuiTableRow-root:not(:last-child)": {
                        "& td": {
                          borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                            `${borderWidth[1]} solid ${borderColor}`,
                        },
                      },
                    }}
                  >
                    <Table columns={columns} rows={formatTutorsForTable()} />
                  </ArgonBox>
                </ArgonBox>
              ) : (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <ArgonTypography variant="body2" color="text">
                    {searchQuery && filteredTutors.length > 0
                      ? "No tutors match this name or phone number."
                      : "No tutors found. Please apply filters to see results."}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </CardContent>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Dialog
        open={Boolean(classPopupTutor)}
        onClose={() => setClassPopupTutor(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #eef2f7",
            px: 2.5,
            py: 1.5,
          }}
        >
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <SchoolIcon sx={{ color: "#1498fc", fontSize: 20 }} />
              <ArgonBox>
                <ArgonTypography variant="button" fontWeight="bold">
                  {classPopupTutor?.name || "Tutor"}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary" display="block">
                  Classes
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
            <IconButton size="small" onClick={() => setClassPopupTutor(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, maxHeight: 360 }}>
          {classPopupTutor?.classes?.length > 0 ? (
            <ArgonBox display="flex" flexDirection="column" gap={1}>
              {classPopupTutor.classes.map((className, classIndex) => (
                <ArgonBox
                  key={`${className}-${classIndex}`}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    backgroundColor: "#f7f9fc",
                    border: "1px solid #eef2f7",
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 16, color: "#1498fc" }} />
                  <ArgonTypography variant="caption" color="text" fontWeight="medium">
                    {className}
                  </ArgonTypography>
                </ArgonBox>
              ))}
            </ArgonBox>
          ) : (
            <ArgonTypography variant="body2" color="text">
              No classes found
            </ArgonTypography>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
